import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";
import { SEO } from "@/components/SEO";
import { api } from "@/lib/api";

const BRANDSTOFFEN = ["Benzine", "Diesel", "Elektrisch", "Hybride", "LPG"];
const TRANSMISSIES = ["Handgeschakeld", "Automaat"];

export default function AanbodPage() {
    const [params, setParams] = useSearchParams();
    const [cars, setCars] = useState([]);
    const [facets, setFacets] = useState({ merken: [] });
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const f = useMemo(() => ({
        merk: params.get("merk") || "",
        brandstof: params.get("brandstof") || "",
        transmissie: params.get("transmissie") || "",
        max_prijs: params.get("max_prijs") || "",
        min_jaar: params.get("min_jaar") || "",
        max_km: params.get("max_km") || "",
        sort: params.get("sort") || "nieuwste",
        q: params.get("q") || "",
    }), [params]);

    useEffect(() => {
        api.get("/cars/facets").then((r) => setFacets(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const qs = Object.fromEntries(Object.entries(f).filter(([_, v]) => v !== ""));
        api.get("/cars", { params: qs })
            .then((r) => setCars(r.data))
            .catch(() => setCars([]))
            .finally(() => setLoading(false));
    }, [f]);

    const update = (k, v) => {
        const np = new URLSearchParams(params);
        if (v === "" || v == null) np.delete(k);
        else np.set(k, v);
        setParams(np);
    };

    const reset = () => setParams(new URLSearchParams());

    return (
        <div>
            <SEO title="Aanbod — alle tweedehands wagens" description="Bekijk alle beschikbare tweedehands wagens van Used Car Selection in Bornem. Filter op merk, brandstof, prijs en bouwjaar." />
            <Navbar />
            <section className="bg-[#0E2C4D] text-white">
                <div className="ucs-container py-16">
                    <div className="label-eyebrow text-[#EC6A0A] mb-3">Aanbod</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight">Onze tweedehands wagens</h1>
                    <p className="mt-3 text-white/70 max-w-2xl">Filter eenvoudig op merk, brandstof, prijs en bouwjaar.</p>
                </div>
            </section>

            <section className="ucs-container py-12 grid md:grid-cols-12 gap-8">
                {/* Filters mobile toggle */}
                <button onClick={() => setOpen(!open)} className="md:hidden inline-flex items-center gap-2 ucs-btn-primary self-start" data-testid="filters-mobile-toggle">
                    <Filter className="w-4 h-4" /> Filters
                </button>

                <aside className={`${open ? "block" : "hidden"} md:block md:col-span-3`} data-testid="filters-sidebar">
                    <div className="bg-white border border-slate-200 p-6 sticky top-24" style={{borderRadius: 2}}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-[#0E2C4D]">Filters</h3>
                            <button onClick={reset} className="text-xs text-[#EC6A0A] font-bold uppercase tracking-wider" data-testid="filter-reset">Reset</button>
                        </div>

                        <FilterGroup label="Zoekterm">
                            <input value={f.q} onChange={(e) => update("q", e.target.value)} placeholder="Bijv. Golf"
                                   className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#EC6A0A]"
                                   style={{borderRadius: 2}} data-testid="filter-q" />
                        </FilterGroup>

                        <FilterGroup label="Merk">
                            <Select value={f.merk} onChange={(v) => update("merk", v)} testid="filter-merk">
                                <option value="">Alle merken</option>
                                {facets.merken?.map((m) => <option key={m.merk} value={m.merk}>{m.merk} ({m.count})</option>)}
                            </Select>
                        </FilterGroup>

                        <FilterGroup label="Brandstof">
                            <Select value={f.brandstof} onChange={(v) => update("brandstof", v)} testid="filter-brandstof">
                                <option value="">Alle</option>
                                {BRANDSTOFFEN.map((b) => <option key={b}>{b}</option>)}
                            </Select>
                        </FilterGroup>

                        <FilterGroup label="Transmissie">
                            <Select value={f.transmissie} onChange={(v) => update("transmissie", v)} testid="filter-transmissie">
                                <option value="">Alle</option>
                                {TRANSMISSIES.map((b) => <option key={b}>{b}</option>)}
                            </Select>
                        </FilterGroup>

                        <FilterGroup label="Max. prijs">
                            <Select value={f.max_prijs} onChange={(v) => update("max_prijs", v)} testid="filter-max-prijs">
                                <option value="">Geen limiet</option>
                                <option value="10000">€ 10.000</option>
                                <option value="20000">€ 20.000</option>
                                <option value="35000">€ 35.000</option>
                                <option value="60000">€ 60.000</option>
                                <option value="100000">€ 100.000</option>
                            </Select>
                        </FilterGroup>

                        <FilterGroup label="Min. bouwjaar">
                            <Select value={f.min_jaar} onChange={(v) => update("min_jaar", v)} testid="filter-min-jaar">
                                <option value="">Geen limiet</option>
                                {[2024, 2022, 2020, 2018, 2015, 2010].map((y) => <option key={y} value={y}>Vanaf {y}</option>)}
                            </Select>
                        </FilterGroup>

                        <FilterGroup label="Max. kilometerstand">
                            <Select value={f.max_km} onChange={(v) => update("max_km", v)} testid="filter-max-km">
                                <option value="">Geen limiet</option>
                                <option value="30000">Tot 30.000 km</option>
                                <option value="60000">Tot 60.000 km</option>
                                <option value="100000">Tot 100.000 km</option>
                                <option value="150000">Tot 150.000 km</option>
                            </Select>
                        </FilterGroup>
                    </div>
                </aside>

                <div className="md:col-span-9">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sm text-slate-600" data-testid="results-count">{loading ? "Laden..." : `${cars.length} resultaten`}</div>
                        <Select value={f.sort} onChange={(v) => update("sort", v)} testid="filter-sort">
                            <option value="nieuwste">Nieuwste eerst</option>
                            <option value="prijs_asc">Prijs (laag-hoog)</option>
                            <option value="prijs_desc">Prijs (hoog-laag)</option>
                            <option value="km_asc">Km (laagste)</option>
                            <option value="jaar_desc">Bouwjaar (nieuwste)</option>
                        </Select>
                    </div>

                    {!loading && cars.length === 0 && (
                        <div className="bg-white border border-slate-200 p-12 text-center" style={{borderRadius: 2}}>
                            <X className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-[#0E2C4D]">Geen resultaten gevonden</h3>
                            <p className="text-sm text-slate-500 mt-2">Pas je filters aan om meer auto's te vinden.</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="aanbod-car-grid">
                        {cars.map((c) => <CarCard key={c.id} car={c} />)}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const FilterGroup = ({ label, children }) => (
    <div className="mb-5">
        <div className="label-eyebrow text-slate-500 mb-2">{label}</div>
        {children}
    </div>
);

const Select = ({ value, onChange, children, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#EC6A0A] bg-white"
            style={{borderRadius: 2}} data-testid={testid}>
        {children}
    </select>
);
